// The ONE table of keyboard shortcuts. The Electron menu's accelerators, the web
// menu's labels and key bindings, the toolbar tooltips, the renderer's key checks
// and the Help ▸ Keyboard shortcuts sheet all read it, so no two of them can
// disagree about which key does what. Pure (no DOM, no Electron): the main process
// and the renderer both import it, and it is unit-tested.
//
// A chord is written `Ctrl+Shift+Alt+Key`, modifiers in that order. `Ctrl` means
// Ctrl on Windows/Linux and Cmd on macOS. A letter is upper-case; a punctuation
// key is written as the character it types (`?`, `[`), and Shift is then implied
// by the character rather than written (`?` not `Shift+/`), so a layout that puts
// `?` elsewhere still matches.

export type ShortcutId =
  | 'addToSelection'
  | 'cancel'
  | 'command'
  | 'constrain'
  | 'contextMenu'
  | 'copy'
  | 'cut'
  | 'cycleSelection'
  | 'delete'
  | 'dragCopy'
  | 'duplicate'
  | 'dynInput'
  | 'finish'
  | 'fullscreen'
  | 'grid'
  | 'gridSnap'
  | 'group'
  | 'help'
  | 'importDrawing'
  | 'newDrawing'
  | 'noSnap'
  | 'nudge'
  | 'nudgeUp'
  | 'openProject'
  | 'ortho'
  | 'osnap'
  | 'pan'
  | 'paste'
  | 'polar'
  | 'print'
  | 'properties'
  | 'redo'
  | 'repeatLast'
  | 'rotateCcw'
  | 'rotateCw'
  | 'saveProject'
  | 'saveProjectAs'
  | 'selectAll'
  | 'typedEntry'
  | 'undo'
  | 'ungroup'
  | 'view2d'
  | 'view3d'
  | 'zoomFit';

export type ShortcutGroup =
  | 'Drawing'
  | 'Edit'
  | 'File'
  | 'Help'
  | 'View';

export interface Shortcut {
  id: ShortcutId;
  /** Chords that trigger it; the first is the one menus and tooltips show. Empty
   *  for a documentation-only row (a gesture, or keys the context decides). */
  keys: string[];
  /** English description for the Help sheet (a DICT key, so it is translated). */
  label: string;
  group: ShortcutGroup;
  /** What the Help sheet shows in place of the chords, when they don't read
   *  well as a list (the arrow keys, a gesture). English; translated too. */
  display?: string;
}

export const SHORTCUTS: readonly Shortcut[] = [
  { id: 'newDrawing', keys: ['Ctrl+N', 'Ctrl+Shift+N'], label: 'New drawing', group: 'File' },
  { id: 'openProject', keys: ['Ctrl+O'], label: 'Open project', group: 'File' },
  { id: 'importDrawing', keys: ['Ctrl+I'], label: 'Import drawing (DXF / DWG / PDF / image)', group: 'File' },
  { id: 'saveProject', keys: ['Ctrl+S'], label: 'Save project', group: 'File' },
  { id: 'saveProjectAs', keys: ['Ctrl+Shift+S'], label: 'Save project as', group: 'File' },
  { id: 'print', keys: ['Ctrl+P'], label: 'Print', group: 'File' },

  { id: 'undo', keys: ['Ctrl+Z'], label: 'Undo', group: 'Edit' },
  { id: 'redo', keys: ['Ctrl+Y', 'Ctrl+Shift+Z'], label: 'Redo', group: 'Edit' },
  { id: 'cut', keys: ['Ctrl+X'], label: 'Cut selection', group: 'Edit' },
  { id: 'copy', keys: ['Ctrl+C'], label: 'Copy selection', group: 'Edit' },
  { id: 'paste', keys: ['Ctrl+V'], label: 'Paste', group: 'Edit' },
  { id: 'duplicate', keys: ['Ctrl+D'], label: 'Duplicate selection', group: 'Edit' },
  { id: 'selectAll', keys: ['Ctrl+A'], label: 'Select all', group: 'Edit' },
  { id: 'group', keys: ['Ctrl+G'], label: 'Group selection', group: 'Edit' },
  { id: 'ungroup', keys: ['Ctrl+Shift+G'], label: 'Ungroup', group: 'Edit' },
  { id: 'delete', keys: ['Delete', 'Backspace'], label: 'Delete selection', group: 'Edit' },
  { id: 'rotateCcw', keys: ['['], label: 'Rotate selection −15°', group: 'Edit' },
  { id: 'rotateCw', keys: [']'], label: 'Rotate selection +15°', group: 'Edit' },
  { id: 'nudge', keys: [], label: 'Nudge selection 10 mm (Shift = 100 mm)', group: 'Edit', display: 'Arrow keys' },
  { id: 'nudgeUp', keys: [], label: 'Nudge selection up / down 10 mm (Shift = 100 mm)', group: 'Edit', display: 'Page Up / Page Down' },
  { id: 'addToSelection', keys: [], label: 'Add to / remove from the selection', group: 'Edit', display: 'Shift-click · Shift-drag a box' },
  { id: 'cycleSelection', keys: ['Tab', 'Shift+Tab'], label: 'Select the next object under the cursor (Alt+click too)', group: 'Edit' },
  { id: 'contextMenu', keys: [], label: 'Context menu (copy · delete · z-order · colours)', group: 'Edit', display: 'Right-click an object' },

  { id: 'command', keys: [], label: 'Type a command (command bar), then Enter / Space', group: 'Drawing', display: 'L · C · REC · DIM · M …' },
  { id: 'repeatLast', keys: ['Enter', 'Space'], label: 'Repeat the last tool (no tool running)', group: 'Drawing' },
  { id: 'finish', keys: [], label: 'Finish the current draw tool', group: 'Drawing', display: 'Enter · right-click · double-click' },
  { id: 'cancel', keys: ['Escape'], label: 'Cancel / deselect', group: 'Drawing' },
  { id: 'typedEntry', keys: [], label: 'Precise distance / coordinate (mm · cm · m · ft · in)', group: 'Drawing', display: 'Type a number while drawing' },
  { id: 'noSnap', keys: [], label: 'Suspend snapping for the next point', group: 'Drawing', display: 'Alt (while drawing)' },
  { id: 'constrain', keys: [], label: '15° steps · a square rectangle · keep proportions when resizing · 15° rotation', group: 'Drawing', display: 'Shift (while drawing or dragging)' },
  { id: 'dragCopy', keys: [], label: 'Leave a copy behind', group: 'Drawing', display: 'Ctrl while dragging' },
  { id: 'osnap', keys: ['F3'], label: 'Toggle object snap', group: 'Drawing' },
  { id: 'grid', keys: ['F7'], label: 'Show / hide the grid', group: 'Drawing' },
  { id: 'ortho', keys: ['F8'], label: 'Toggle Ortho', group: 'Drawing' },
  { id: 'gridSnap', keys: ['F9'], label: 'Toggle snap to grid', group: 'Drawing' },
  { id: 'polar', keys: ['F10'], label: 'Cycle Polar tracking', group: 'Drawing' },
  { id: 'dynInput', keys: ['F12'], label: 'Show / hide the length & angle readout at the cursor', group: 'Drawing' },

  { id: 'zoomFit', keys: ['Ctrl+0'], label: 'Zoom to fit', group: 'View' },
  { id: 'view2d', keys: ['Ctrl+2'], label: '2D drawing', group: 'View' },
  { id: 'view3d', keys: ['Ctrl+3'], label: '3D preview', group: 'View' },
  { id: 'properties', keys: ['Ctrl+1'], label: 'Show / hide the Properties panel', group: 'View' },
  { id: 'pan', keys: [], label: 'Pan the view', group: 'View', display: 'Space-drag · middle-drag · right-drag' },
  { id: 'fullscreen', keys: ['F11'], label: 'Toggle full screen', group: 'View' },

  { id: 'help', keys: ['?'], label: 'Show / hide this help', group: 'Help' },
];

const BY_ID = new Map(SHORTCUTS.map((s) => [s.id, s]));

export function shortcut(id: ShortcutId): Shortcut {
  return BY_ID.get(id)!;
}

/** The subset of a KeyboardEvent a chord is read from. */
export interface KeyLike { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean }

const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'CapsLock']);

/** The canonical chord a key event spells, or '' for a bare modifier press. */
export function chordOf(e: KeyLike): string {
  if (MODIFIER_KEYS.has(e.key)) return '';
  let key = e.key === ' ' ? 'Space' : e.key;
  const printable = key.length === 1;
  const letter = /^[a-z]$/i.test(key);
  if (letter) key = key.toUpperCase();
  const mods: string[] = [];
  if (e.ctrlKey || e.metaKey) mods.push('Ctrl');
  // A printable non-letter already says whether Shift was held (`?` vs `/`).
  if (e.shiftKey && (!printable || letter)) mods.push('Shift');
  if (e.altKey) mods.push('Alt');
  return [...mods, key].join('+');
}

/** Whether the key event is one of the shortcut's chords. */
export function matchesShortcut(e: KeyLike, id: ShortcutId): boolean {
  const c = chordOf(e);
  return c !== '' && shortcut(id).keys.includes(c);
}

/** The shortcut a key event triggers, if any (documentation rows never match). */
export function shortcutFor(e: KeyLike): Shortcut | null {
  const c = chordOf(e);
  if (!c) return null;
  return SHORTCUTS.find((s) => s.keys.includes(c)) ?? null;
}

const KEY_LABEL: Record<string, string> = { Delete: 'Del', Escape: 'Esc', Backspace: '⌫' };
/** Key and modifier names a keyboard prints differently per language (Italian
 *  `Canc` / `Maiusc`, French `Suppr` / `Maj`), handed to the caller's translator. */
const TRANSLATED_KEYS = new Set(['Del', 'Shift', 'Enter', 'Space']);

type Translate = (en: string) => string;
const same: Translate = (en) => en;

/** How a chord reads in a menu or tooltip: `Ctrl+Shift+N`, `Del`, `F8`. `t`
 *  translates the key names that differ by language. */
export function chordLabel(chord: string, t: Translate = same): string {
  const parts = chord.split('+');
  const key = parts.pop()!;
  const name = (k: string): string => (TRANSLATED_KEYS.has(k) ? t(k) : k);
  return [...parts, KEY_LABEL[key] ?? key].map(name).join('+');
}

/** The one chord a menu item or tooltip shows for the shortcut ('' when none). */
export function shortcutLabel(id: ShortcutId, t: Translate = same): string {
  const k = shortcut(id).keys[0];
  return k ? chordLabel(k, t) : '';
}

/** Every chord of the shortcut, for the Help sheet (`Ctrl+Y / Ctrl+Shift+Z`). */
export function shortcutKeysLabel(s: Shortcut, t: Translate = same): string {
  return s.keys.map((k) => chordLabel(k, t)).join('  /  ');
}

/** The Electron accelerator for the shortcut's first chord, or undefined. */
export function electronAccelerator(id: ShortcutId): string | undefined {
  const k = shortcut(id).keys[0];
  if (!k) return undefined;
  const parts = k.split('+');
  let key = parts.pop()!;
  // Electron wants the unshifted key plus Shift for a shifted character.
  const shifted: Record<string, string> = { '?': '/' };
  if (shifted[key]) { parts.push('Shift'); key = shifted[key]; }
  return [...parts.map((m) => (m === 'Ctrl' ? 'CmdOrCtrl' : m)), key].join('+');
}

/** The mouse gestures, listed beside the keys on Help ▸ Keyboard shortcuts. Not
 *  bindings (the handlers own them) — the one place their wording lives, so the
 *  sheet can list and search them. `how` and `label` are English, translated by
 *  the sheet. */
export interface Gesture { how: string; label: string }
export const GESTURES: readonly Gesture[] = [
  { how: 'Click', label: 'Select an object' },
  { how: 'Shift+click', label: 'Add to or remove from the selection' },
  { how: 'Drag on empty space', label: 'Select with a marquee' },
  { how: 'Shift+drag on empty space', label: 'Add a marquee to the selection' },
  { how: 'Tab or Alt+click', label: 'Pick the next object under the cursor' },
  { how: 'Space+drag, middle-drag or right-drag', label: 'Pan the view' },
  { how: 'Mouse wheel', label: 'Zoom in or out' },
  { how: 'Ctrl+drag', label: 'Leave a copy behind' },
  { how: 'Alt while drawing or dragging', label: 'Suspend snapping' },
  { how: 'Shift while drawing', label: 'Constrain to 15° steps, a square or the proportions' },
  { how: 'Double-click a dimension figure', label: 'Type a new length' },
  { how: 'Right-click a pinned figure', label: 'Release it' },
  { how: 'Drag a wall-chain joint', label: 'Move a door or pane edge' },
  { how: 'Drag a connection’s tag', label: 'Slide it along its line' },
  { how: 'Drag a ceiling’s arrow onto a wall', label: 'Turn its slope, ridge or barrel' },
  { how: 'Drag an edge of a truncated pyramid’s flat part', label: 'Change its setback from that wall' },
  { how: 'Hover a heater’s gap edge', label: 'Add or clear a guard cover' },
  { how: 'Double-click an underlay', label: 'Move, scale or rotate it' },
  { how: 'Right-click the Snap button', label: 'Choose which snaps are on' },
  { how: 'Drag a row in the Parts tab', label: 'Change the drawing order' },
  { how: 'Drag a sheet’s thumbnail or tab', label: 'Reorder the sheets' },
  { how: 'Click a catalogue card', label: 'Place it with the next click' },
];
